/**
 * Mapea códigos internos de commands de Gastos a mensaje friendly.
 * Preserva `code` para Sentry; nunca devolver el raw al usuario.
 * Paridad: `mapCashCommandError` (Caja / ADR-034) · `mapSaleVoidRemoteError` (Ventas).
 */

export type MappedExpenseCommandError = {
  code: string;
  userMessage: string;
  expected?: boolean;
};

const EXPENSE_ERROR_MAP: Array<{
  match: RegExp;
  code: string;
  userMessage: string;
  expected?: boolean;
}> = [
  {
    match: /expenses_module_disabled/i,
    code: 'expenses_module_disabled',
    userMessage: 'Gastos no está disponible en este momento.',
    expected: true,
  },
  {
    match: /expense_not_found/i,
    code: 'expense_not_found',
    userMessage: 'No encontré ese gasto. Refrescá la lista e intentá de nuevo.',
    expected: true,
  },
  {
    match: /expense_not_active/i,
    code: 'expense_not_active',
    userMessage: 'Ese gasto no está activo.',
    expected: true,
  },
  {
    match: /expense_fiscal_already_set/i,
    code: 'expense_fiscal_already_set',
    userMessage: 'Ese gasto ya tiene comprobante fiscal.',
    expected: true,
  },
  {
    match: /expense_has_active_settlements/i,
    code: 'expense_has_active_settlements',
    userMessage: 'Anulá los pagos primero.',
    expected: true,
  },
  {
    match: /invalid_amount|invalid_settlement_amount/i,
    code: 'invalid_amount',
    userMessage: 'Ingresá un monto válido mayor a cero.',
    expected: true,
  },
  {
    match: /settlementId_required|settlement_method_required/i,
    code: 'settlement_fields_required',
    userMessage: 'Faltan datos del pago. Reintentá o cargá el gasto como pendiente.',
    expected: true,
  },
  {
    match: /cash_session_required|cash_session_not_open/i,
    code: 'cash_session_not_open',
    userMessage: 'Abrí la caja para pagar en efectivo.',
    expected: true,
  },
  {
    match: /cash_session_closed/i,
    code: 'cash_session_closed',
    userMessage: 'Ese turno ya está cerrado. No se puede anular el egreso de caja.',
    expected: true,
  },
  {
    match: /no_open_cash_session/i,
    code: 'no_open_cash_session',
    userMessage: 'Abrí una caja para registrar la devolución del dinero.',
    expected: true,
  },
  {
    match: /cash_outflows_disabled/i,
    code: 'cash_outflows_disabled',
    userMessage: 'Los egresos de caja no están disponibles en este momento.',
    expected: true,
  },
  {
    match: /invalid_invoice_doc_type|invalid_invoice_pv|invalid_invoice_number|invalid_supplier_tax_id|invalid_invoice_iva_breakdown|invalid_invoice_perceptions|expenses_fiscal_invoice/i,
    code: 'invalid_fiscal_invoice',
    userMessage: 'Revisá los datos del comprobante fiscal.',
    expected: true,
  },
  {
    match: /invalid_expense_category/i,
    code: 'invalid_expense_category',
    userMessage: 'Elegí una categoría válida.',
    expected: true,
  },
  {
    match: /module_permission_denied|permission|forbidden|denied|not.?allowed/i,
    code: 'expense_permission_denied',
    userMessage: 'No tenés permiso para esta acción de gastos.',
    expected: true,
  },
  {
    match: /operationid_required/i,
    code: 'operationId_required',
    userMessage: 'No se pudo completar. Reintentá en unos segundos.',
  },
  {
    match: /unsupported_operation/i,
    code: 'unsupported_operation',
    userMessage: 'Esa operación no está disponible. Actualizá la app.',
  },
];

export function mapExpenseCommandError(raw: string): MappedExpenseCommandError {
  const msg = (raw || '').trim();
  for (const row of EXPENSE_ERROR_MAP) {
    if (row.match.test(msg)) {
      return { code: row.code, userMessage: row.userMessage, expected: row.expected };
    }
  }
  // Mensaje ya friendly (toast/UI) — no pisarlo con el genérico
  if (msg && !/^[a-z][a-z0-9_]*$/i.test(msg) && /[A-Za-záéíóúñÁÉÍÓÚÑ ]/.test(msg)) {
    return { code: 'expense_command_message', userMessage: msg, expected: true };
  }
  return {
    code: msg ? 'expense_command_unknown' : 'expense_command_empty',
    userMessage: 'No se pudo completar la operación de gastos. Reintentá o pedile a soporte.',
  };
}

export function rawExpenseErrorText(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message ?? '');
  }
  return String(error ?? '');
}

export function expenseCommandErrorFromRaw(raw: string): Error & {
  code: string;
  expected?: boolean;
} {
  const mapped = mapExpenseCommandError(raw);
  const err = new Error(mapped.userMessage) as Error & { code: string; expected?: boolean };
  err.code = mapped.code;
  if (mapped.expected) err.expected = true;
  return err;
}
